/**
 * Users Page
 *
 * Main admin interface for managing organization users.
 * Composes the TopBar, UserDataGrid, and InviteUserDialog into
 * a complete user management experience.
 *
 * Features:
 * - TopBar navigation
 * - Page heading and "Invite user" action button
 * - Server-paginated user list
 * - Loading skeleton state on initial load
 * - Dismissible error alert with retry on failure
 * - Stale-data-on-failure (existing rows preserved on refresh errors)
 * - Snackbar for invite/resend success notifications
 *
 * @module pages/UsersPage
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TopBar } from '../components/TopBar';
import { UserDataGrid } from '../components/UserDataGrid';
import { InviteUserDialog } from '../components/InviteUserDialog';
import { ConfirmDeleteUserDialog } from '../components/ConfirmDeleteUserDialog';
import { ConfirmChangeRoleDialog } from '../components/ConfirmChangeRoleDialog';
import { TenantDetailsPanel } from '../components/TenantDetailsPanel';
import { ExternalAccessPanel } from '../components/ExternalAccessPanel';
import type { User } from '../types';
import {
  useUsersStore,
  CANNOT_MANAGE_MEMBERS_MESSAGE,
} from '../stores/useUsersStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useOrgsStore } from '../stores/useOrgsStore';
import { useExternalAccessStore } from '../stores/useExternalAccessStore';
import { formatErrorMessage } from '../utils';

// =============================================================================
// Constants
// =============================================================================

/**
 * Number of skeleton rows to display during the initial loading state.
 */
const SKELETON_ROW_COUNT = 8;

/**
 * Auto-hide duration (in milliseconds) for the success snackbar.
 */
const SNACKBAR_AUTO_HIDE_MS = 4000;

// =============================================================================
// Loading Skeleton
// =============================================================================

/**
 * UsersPageSkeleton - Loading skeleton that approximates the data grid layout.
 *
 * Renders a header row of column-width skeletons followed by repeated rows.
 * Used during the initial data load before any user data is available.
 */
function UsersPageSkeleton() {
  return (
    <Box data-testid="users-page-skeleton" sx={{ width: '100%' }}>
      {/* Header row skeleton */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 1, px: 1, py: 1.5 }}
        data-testid="users-page-skeleton-header"
      >
        <Skeleton variant="text" width="20%" height={24} />
        <Skeleton variant="text" width="18%" height={24} />
        <Skeleton variant="text" width="10%" height={24} />
        <Skeleton variant="text" width="18%" height={24} />
        <Skeleton variant="text" width="10%" height={24} />
        <Skeleton variant="text" width="14%" height={24} />
      </Stack>

      {/* Data row skeletons */}
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={2}
          sx={{
            px: 1,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
          data-testid="users-page-skeleton-row"
        >
          <Skeleton variant="text" width="20%" height={20} />
          <Skeleton variant="text" width="18%" height={20} />
          <Skeleton variant="rounded" width="10%" height={20} />
          <Skeleton variant="text" width="18%" height={20} />
          <Skeleton variant="rounded" width="10%" height={20} />
          <Skeleton variant="rounded" width="14%" height={28} />
        </Stack>
      ))}
    </Box>
  );
}

// =============================================================================
// UsersPage Component
// =============================================================================

/**
 * UsersPage - Admin page for managing organization users.
 *
 * Composes the user management interface:
 * - TopBar at the top
 * - Page heading + "Invite user" button row
 * - Error alert (with retry) when load fails
 * - Loading skeletons during initial load
 * - UserDataGrid with paginated user list
 * - InviteUserDialog (modal) for adding new users
 * - Success Snackbar for invite/resend confirmations
 *
 * Protected by AuthGuard and AdminOrStaffGuard at the route level.
 */
export function UsersPage() {
  // ---------------------------------------------------------------------------
  // Store state and actions
  // ---------------------------------------------------------------------------
  const users = useUsersStore((state) => state.users);
  const pagination = useUsersStore((state) => state.pagination);
  const isLoading = useUsersStore((state) => state.isLoading);
  const error = useUsersStore((state) => state.error);
  const successMessage = useUsersStore((state) => state.successMessage);
  // Partial-success warning (e.g. an invited admin whose org-management
  // membership couldn't be granted). Surfaced in its own warning snackbar so
  // it's never silently dropped.
  const warningMessage = useUsersStore((state) => state.warningMessage);
  // Proactive per-org invite capability (authority + entitlement), resolved by
  // the backend and delivered on the list response. This is the source of truth
  // for whether member management is available in the currently-viewed org.
  const canManageTargetOrg = useUsersStore((state) => state.canManageTargetOrg);
  const targetOrgId = useUsersStore((state) => state.targetOrgId);

  // Staff mode: a platform staff admin manages other orgs' members via the
  // picker (gated solely on the v1 claim's platform.staff.admin role).
  const isStaffAdmin = useAuthStore((state) => state.isStaffAdmin);
  // Instance-level tenant-management authority gates the Delete-tenant affordance
  // inside the details tab.
  const canManageTenants = useAuthStore((state) => state.canManageTenants);
  // The caller's own home org — the details tab falls back to it when no picker
  // selection is active (a normal admin has no picker).
  const activeOrgId = useAuthStore((state) => state.activeOrgId);
  // The current user's own ZITADEL id (ID-token `sub`), used to disable the
  // remove action on their own row (admins can't remove themselves).
  const currentUserId = useAuthStore((state) => state.claims?.sub ?? null);

  const loadUsers = useUsersStore((state) => state.loadUsers);
  const setPage = useUsersStore((state) => state.setPage);
  const setPageSize = useUsersStore((state) => state.setPageSize);
  const resendInvite = useUsersStore((state) => state.resendInvite);
  const deleteUser = useUsersStore((state) => state.deleteUser);
  const updateUserRole = useUsersStore((state) => state.updateUserRole);
  const setTargetOrg = useUsersStore((state) => state.setTargetOrg);
  const clearSuccessMessage = useUsersStore(
    (state) => state.clearSuccessMessage,
  );
  const clearWarningMessage = useUsersStore(
    (state) => state.clearWarningMessage,
  );
  const clearError = useUsersStore((state) => state.clearError);
  const loadOrgs = useOrgsStore((state) => state.loadOrgs);

  // ---------------------------------------------------------------------------
  // Local UI state
  // ---------------------------------------------------------------------------
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  // The member queued for removal drives the confirm dialog (null = closed).
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  // The member queued for a role toggle drives its confirm dialog (null = closed).
  const [userToChangeRole, setUserToChangeRole] = useState<User | null>(null);
  // Which tab is active. "details" surfaces the tenant-details view (staff only).
  const [activeTab, setActiveTab] = useState<'members' | 'details'>('members');

  // External access (EPIC-AUTH-008): the outside accounts holding a grant that
  // belongs to the viewed organization. A separate list from the roster, and a
  // separate load — a failure here never blanks the members grid.
  const externalItems = useExternalAccessStore((state) => state.items);
  const externalTruncated = useExternalAccessStore((state) => state.truncated);
  const externalCanManage = useExternalAccessStore((state) => state.canManage);
  const externalLoading = useExternalAccessStore((state) => state.isLoading);
  const externalError = useExternalAccessStore((state) => state.error);
  const loadExternalAccess = useExternalAccessStore((state) => state.load);
  const grantExternalAccess = useExternalAccessStore((state) => state.grant);
  const revokeExternalAccess = useExternalAccessStore((state) => state.revoke);
  const setExternalAccessRole = useExternalAccessStore((state) => state.setRole);
  // Confirmation message after a tenant delete (own snackbar).
  const [tenantMessage, setTenantMessage] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /**
   * Load users on component mount.
   *
   * Normal admins load their active org's members immediately. **Staff admins**
   * must first pick an org from the OrgPicker — selecting one drives the load
   * via `setTargetOrg`, so we deliberately do NOT auto-load here for them
   * (there's no org to load yet, and we must never default a staff user into an
   * arbitrary org's data).
   *
   * We intentionally fire-and-forget the promise; the store manages its own
   * isLoading and error state. `loadUsers`/`isStaffAdmin` are stable, so this
   * effectively runs once on mount.
   */
  useEffect(() => {
    if (!isStaffAdmin) {
      void loadUsers();
    }
  }, [isStaffAdmin, loadUsers]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /**
   * Opens the invite user dialog.
   */
  const handleOpenInviteDialog = useCallback(() => {
    setInviteDialogOpen(true);
  }, []);

  /**
   * Closes the invite user dialog.
   * Used both for cancellation and after a successful invite.
   */
  const handleCloseInviteDialog = useCallback(() => {
    setInviteDialogOpen(false);
  }, []);

  /**
   * Handles a retry attempt after a failed load.
   * Clears the existing error and triggers loadUsers again.
   */
  const handleRetry = useCallback(() => {
    clearError();
    void loadUsers();
  }, [clearError, loadUsers]);

  /**
   * Opens the remove-user confirmation dialog for the given member.
   */
  const handleRequestDelete = useCallback((user: User) => {
    setUserToDelete(user);
  }, []);

  /**
   * Closes the remove-user confirmation dialog (cancel or after success).
   */
  const handleCloseDeleteDialog = useCallback(() => {
    setUserToDelete(null);
  }, []);

  /**
   * Opens the role-change confirmation dialog for the given member.
   */
  const handleRequestRoleChange = useCallback((user: User) => {
    setUserToChangeRole(user);
  }, []);

  /**
   * Closes the role-change confirmation dialog (cancel or after success).
   */
  const handleCloseRoleDialog = useCallback(() => {
    setUserToChangeRole(null);
  }, []);

  /**
   * Handles the snackbar close event.
   *
   * MUI calls this with a reason; we ignore reason and always clear the
   * success message so it doesn't reappear on remount.
   */
  const handleSnackbarClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      // Don't dismiss on clickaway to avoid accidental loss of confirmation
      if (reason === 'clickaway') {
        return;
      }
      clearSuccessMessage();
    },
    [clearSuccessMessage],
  );

  /**
   * Handles the warning snackbar close event. Mirrors the success handler but
   * clears the warning channel. Like the success snackbar, it ignores clickaway
   * so a partial-success caveat isn't lost to an accidental background click.
   */
  const handleWarningSnackbarClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      clearWarningMessage();
    },
    [clearWarningMessage],
  );

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  /**
   * Staff admin who hasn't picked an org yet: show a prompt instead of the
   * grid, and don't allow inviting (there's no target org to invite into).
   */
  const staffNeedsOrgSelection = isStaffAdmin && !targetOrgId;

  /**
   * Show the skeleton only during the initial load (no data yet). Suppressed
   * while a staff admin still has to pick an org (nothing is loading yet).
   */
  const showInitialSkeleton =
    isLoading && users.length === 0 && !error && !staffNeedsOrgSelection;

  /**
   * The invite action requires a target org the caller can manage. Disabled when
   * a staff admin hasn't picked an org yet, or when the caller lacks (or hasn't
   * yet resolved) member-management capability in the viewed org — both `null`
   * (unresolved, still loading) and `false` (denied) disable it, so the action
   * fails closed while a load is in flight.
   */
  const inviteDisabled = staffNeedsOrgSelection || !canManageTargetOrg;

  /**
   * Whether the caller can't manage members in the viewed org — drives the
   * informational notice explaining the disabled invite button. Gated on an
   * explicit `=== false` (a resolved denial), NOT on the unresolved `null`, so a
   * capable caller never sees a false-negative notice flash before the first
   * load resolves. Distinct from the "staff hasn't picked an org yet" prompt.
   */
  const cannotManageThisOrg =
    !staffNeedsOrgSelection && canManageTargetOrg === false;

  /**
   * Whether member-mutating actions (remove) are available. Mirrors the invite
   * gating: an org must be in scope and the caller must hold member-management
   * capability there (canManageTargetOrg resolved `true`). The backend remains
   * the source of truth and re-authorizes every call; this is the client-side
   * affordance gate.
   */
  const canManageMembers =
    !staffNeedsOrgSelection && canManageTargetOrg === true;

  /**
   * Format the error message for display.
   */
  const errorMessage = error ? formatErrorMessage(error) : null;

  /**
   * The org whose tenant details the details tab shows: the staff-picked org, or
   * (for a normal admin) their own home org.
   */
  const selectedOrgId = targetOrgId ?? activeOrgId;

  /**
   * Load external access for whichever organization is in view, and reload when
   * that changes. Separate from the members load on purpose: the two are
   * different questions, and a failure of this one must not blank the roster.
   */
  useEffect(() => {
    if (selectedOrgId) {
      void loadExternalAccess();
    }
  }, [selectedOrgId, loadExternalAccess]);

  /**
   * After a tenant is deleted: refresh the picker list, drop the now-stale
   * selection, return to the Members tab, and confirm via snackbar.
   */
  const handleTenantDeleted = useCallback(
    (message: string) => {
      setTenantMessage(message);
      setActiveTab('members');
      void setTargetOrg(null);
      void loadOrgs();
    },
    [setTargetOrg, loadOrgs],
  );

  /**
   * The details tab is a staff-only surface (they have the org picker). A staff
   * admin who hasn't picked an org yet sees a prompt instead.
   */
  const showTabs = isStaffAdmin;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
      data-testid="users-page"
    >
      {/* Top navigation bar */}
      <TopBar />

      {/* Main content area */}
      <Container
        maxWidth="lg"
        sx={{ flexGrow: 1, py: 4 }}
        data-testid="users-page-content"
      >
        {/* Members / Tenant details tabs (staff only — they have the org picker
            that selects which tenant these tabs act on). */}
        {showTabs && (
          <Tabs
            value={activeTab}
            onChange={(_e, value: 'members' | 'details') => setActiveTab(value)}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            data-testid="users-page-tabs"
          >
            <Tab
              label="Members"
              value="members"
              data-testid="users-page-tab-members"
            />
            <Tab
              label="Tenant details"
              value="details"
              data-testid="users-page-tab-details"
            />
          </Tabs>
        )}

        {/* Tenant details tab body */}
        {showTabs && activeTab === 'details' ? (
          staffNeedsOrgSelection || !selectedOrgId ? (
            <Alert severity="info" data-testid="tenant-details-select-org">
              Select an organization above to view its details.
            </Alert>
          ) : (
            <TenantDetailsPanel
              key={selectedOrgId}
              orgId={selectedOrgId}
              canManageTenants={canManageTenants}
              onDeleted={handleTenantDeleted}
              onUpdated={setTenantMessage}
            />
          )
        ) : (
          <>
            {/* Header row: page title + invite button */}
            <Stack
              direction="row"
              data-testid="users-page-header"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                data-testid="users-page-title"
              >
                Users
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenInviteDialog}
                disabled={inviteDisabled}
                data-testid="users-page-invite-button"
              >
                Invite user
              </Button>
            </Stack>

            {/* The org picker (staff admins) now lives in the TopBar, where it
            replaces the static org-name display. It still drives `targetOrgId`
            via the users store, so the staff selection flow is unchanged. */}

            {/* Capability notice: the caller can't manage members in the viewed org
            (lacks ZITADEL authority, or the org isn't entitled). Shown on the page
            (not just the dialog) so the disabled invite button is explained —
            informational severity, not an error. */}
            {cannotManageThisOrg && (
              <Alert
                severity="info"
                sx={{ mb: 3 }}
                data-testid="users-page-not-entitled"
              >
                {CANNOT_MANAGE_MEMBERS_MESSAGE}
              </Alert>
            )}

            {/* Error alert with retry (dismissible) */}
            {errorMessage && (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
                data-testid="users-page-error"
                action={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetry}
                      data-testid="users-page-retry-button"
                    >
                      Retry
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="Close"
                      color="inherit"
                      onClick={clearError}
                      data-testid="users-page-error-close"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                }
              >
                {errorMessage}
              </Alert>
            )}

            {/* Body: staff prompt, skeleton, or grid */}
            {staffNeedsOrgSelection ? (
              <Alert
                severity="info"
                sx={{ mb: 3 }}
                data-testid="users-page-select-org"
              >
                Select an organization above to view and manage its members.
              </Alert>
            ) : showInitialSkeleton ? (
              <UsersPageSkeleton />
            ) : (
              <UserDataGrid
                users={users}
                pagination={pagination}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                onResendInvite={resendInvite}
                onRequestDelete={handleRequestDelete}
                canDelete={canManageMembers}
                onRequestRoleChange={handleRequestRoleChange}
                canChangeRole={canManageMembers}
                currentUserId={currentUserId}
                isLoading={isLoading}
              />
            )}

            {/* External access — a separate list, below the roster. Its table
            hides when the service returns no rows (D6); the grant action does
            not, so the first grant stays reachable. */}
            {!staffNeedsOrgSelection && (
              <ExternalAccessPanel
                items={externalItems}
                truncated={externalTruncated}
                canManage={externalCanManage}
                isLoading={externalLoading}
                error={externalError}
                onGrant={grantExternalAccess}
                onRevoke={revokeExternalAccess}
                onSetRole={setExternalAccessRole}
              />
            )}
          </>
        )}
      </Container>

      {/* Invite user dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
      />

      {/* Remove user confirmation dialog */}
      <ConfirmDeleteUserDialog
        user={userToDelete}
        onClose={handleCloseDeleteDialog}
        onConfirm={deleteUser}
      />

      {/* Role-change confirmation dialog */}
      <ConfirmChangeRoleDialog
        user={userToChangeRole}
        onClose={handleCloseRoleDialog}
        onConfirm={updateUserRole}
      />

      {/* Success snackbar */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="users-page-snackbar"
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
          data-testid="users-page-snackbar-alert"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Partial-success warning snackbar. No auto-hide: a caveat like "the
          invited admin couldn't be granted member-management authority" must be
          read and dismissed deliberately, not flashed and lost. */}
      <Snackbar
        open={Boolean(warningMessage)}
        onClose={handleWarningSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="users-page-warning-snackbar"
      >
        <Alert
          onClose={handleWarningSnackbarClose}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
          data-testid="users-page-warning-snackbar-alert"
        >
          {warningMessage}
        </Alert>
      </Snackbar>

      {/* Tenant-delete confirmation snackbar */}
      <Snackbar
        open={Boolean(tenantMessage)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setTenantMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        data-testid="users-page-tenant-snackbar"
      >
        <Alert
          onClose={() => setTenantMessage(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {tenantMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
